import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import * as graphql from 'generated/graphql';

const Container = styled(Flex)`
	align-items: flex-start;
	justify-content: flex-start;
`;

const Party = styled(Flex)<{ small?: boolean }>`
	align-items: center;
	${(props) =>
		props.small
			? `
		flex-basis: 0;
		flex-grow: 1;
		flex-shrink: 1;
		`
			: `
		flex-grow: 0;
		flex-shrink: 0;
		width: 240px;
		`}
`;

const Badge = styled.div<{ party: graphql.EvaluationSubmissionParty }>`
	position: absolute;
	width: 16px;
	height: 16px;
	border-radius: 8px;
	bottom: -2px;
	right: -2px;
	background: ${(props) =>
		props.party === graphql.EvaluationSubmissionParty.Employee
			? props.theme.colors.employee
			: props.theme.colors.manager};
	border: 2px solid white;
`;

const PersonName = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 14px;
`;

const PersonStatus = styled.div<{ green?: boolean; small?: boolean }>`
	color: ${(props) => (props.green ? props.theme.colors.green : props.theme.colors.lightBlueDark)};
	font-size: ${(props) => (props.small ? 12 : 14)}px;
`;

export default function EvaluationParties(props: {
	submission: graphql.EvaluationSubmission;
	small?: boolean;
}) {
	const { t } = useTranslation();

	const managerName = [
		props.submission.manager.user.firstName,
		props.submission.manager.user.lastName,
	].join(' ');

	const employeeName = [
		props.submission.employee.user.firstName,
		props.submission.employee.user.lastName,
	].join(' ');

	return (
		<Container>
			<Party small={props.small}>
				<Box style={{ position: 'relative' }} flexShrink={0} flexGrow={0}>
					<PersonAvatar person={props.submission.employee.user} size={PersonAvatarSize.Medium} />
					<Badge party={graphql.EvaluationSubmissionParty.Employee} />
				</Box>
				<Box ml={3} flexShrink={1} flexGrow={1}>
					{!props.small && <PersonName>{employeeName}</PersonName>}
					{props.submission.employee.requiresInput &&
						props.submission.employee.status === graphql.EvaluationSubmissionStatus.Draft && (
							<PersonStatus small={props.small}>
								{t('evaluation.submission.status.draft')}
							</PersonStatus>
						)}
					{props.submission.employee.requiresInput &&
						props.submission.employee.status === graphql.EvaluationSubmissionStatus.Shared && (
							<PersonStatus small={props.small} green>
								{t('evaluation.submission.status.shared')}
							</PersonStatus>
						)}
					{!props.submission.employee.requiresInput &&
						(props.submission.employee.status === graphql.EvaluationSubmissionStatus.Draft ||
							props.submission.employee.status === graphql.EvaluationSubmissionStatus.Shared) && (
							<PersonStatus small={props.small}>
								{t('evaluation.submission.status.noInputRequired')}
							</PersonStatus>
						)}
					{props.submission.employee.status === graphql.EvaluationSubmissionStatus.Signed && (
						<PersonStatus small={props.small} green>
							{t('evaluation.submission.status.signed')}
						</PersonStatus>
					)}
				</Box>
			</Party>
			<Party small={props.small}>
				<Box style={{ position: 'relative' }} flexShrink={0} flexGrow={0}>
					<PersonAvatar person={props.submission.manager.user} size={PersonAvatarSize.Medium} />
					<Badge party={graphql.EvaluationSubmissionParty.Manager} />
				</Box>
				<Box ml={3} flexShrink={1} flexGrow={1}>
					{!props.small && <PersonName>{managerName}</PersonName>}
					{props.submission.manager.requiresInput &&
						props.submission.manager.status === graphql.EvaluationSubmissionStatus.Draft && (
							<PersonStatus small={props.small}>
								{t('evaluation.submission.status.draft')}
							</PersonStatus>
						)}
					{props.submission.manager.requiresInput &&
						props.submission.manager.status === graphql.EvaluationSubmissionStatus.Shared && (
							<PersonStatus small={props.small} green>
								{t('evaluation.submission.status.shared')}
							</PersonStatus>
						)}
					{!props.submission.manager.requiresInput &&
						(props.submission.manager.status === graphql.EvaluationSubmissionStatus.Draft ||
							props.submission.manager.status === graphql.EvaluationSubmissionStatus.Shared) && (
							<PersonStatus small={props.small}>
								{t('evaluation.submission.status.noInputRequired')}
							</PersonStatus>
						)}
					{props.submission.manager.status === graphql.EvaluationSubmissionStatus.Signed && (
						<PersonStatus small={props.small} green>
							{t('evaluation.submission.status.signed')}
						</PersonStatus>
					)}
				</Box>
			</Party>
		</Container>
	);
}
